import React, { useState } from 'react';
import {
  Modal,
  Box,
  Typography,
  IconButton,
  Button,
  Grid,
  Divider,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import styles from './featuresModal.module.scss';
import {FeaturesModalProps } from '../../../../../interfaces/pages';

const FeaturesModal = ({
  open,
  onClose,
  additionalCharacters,
  selectedFeatureIds,
  setSelectedFeatureIds,
  getAdditionalCharacters,
  onSave
}: FeaturesModalProps) => {
  return (
    <Modal open={open} onClose={onClose} className={styles.featuresModalContainer}>
      <Box sx={{
        position: 'absolute',
        top: '50%', left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 800, maxWidth: '90%',
        bgcolor: 'background.paper',
        boxShadow: 24,
        borderRadius: 2,
        height: '700px',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        pt: 3, pb: 3, pl: 1
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2 }}>
          <Typography variant="h6">Seleccione características adicionales para incluir</Typography>
          <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
        </Box>

        <Box sx={{
          flex: 1, overflowY: 'auto', p: 2,
          '&::-webkit-scrollbar': { width: '8px' },
          '&::-webkit-scrollbar-track': { background: '#f1f1f1', borderRadius: '4px' },
          '&::-webkit-scrollbar-thumb': { background: 'rgba(113, 0, 255, 0.2)', borderRadius: '4px' }
        }}>
          {/* Mostrar mensaje de carga si estamos esperando las características */}
          {(!additionalCharacters || additionalCharacters.length === 0) && (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
              <Typography variant="body1" sx={{ textAlign: 'center', color: 'text.secondary', mb: 2 }}>
                Cargando características adicionales...
              </Typography>
              <Button 
                variant="contained" 
                onClick={() => getAdditionalCharacters()}
                sx={{ borderRadius: '20px', px: 3, backgroundColor: '#7100FF', '&:hover': { backgroundColor: '#5c00d6' } }}
              >
                Recargar características
              </Button>
            </Box>
          )}
          
          {/* Mostrar las características si están disponibles */}
          {Array.isArray(additionalCharacters) && additionalCharacters.length > 0 && (
            <>
              {/* Listar todas las categorías de características */}
              {additionalCharacters.map((category: any, index: number) => {
                // Verificar si la categoría tiene características válidas
                const hasValidFeatures = Array.isArray(category?.value) && category.value.some((feature: any) => feature && feature.code);
                
                if (!hasValidFeatures) return null;
                
                return (
                  <Box key={category?.code || `category-${index}`} sx={{ mb: 3 }}>
                    <Typography 
                      variant="subtitle1" 
                      fontWeight="bold" 
                      sx={{ 
                        mb: 1, 
                        fontFamily: '"LTSaeada", "Roboto", sans-serif',
                        color: '#1D1B20'
                      }}
                    >
                      {category?.name || 'Categoría'}
                    </Typography>
                    <Grid container spacing={2}>
                      {Array.isArray(category?.value) && category.value.map((feature: any, featureIndex: number) => {
                        // Verificar que la característica tenga un código válido
                        if (!feature || !feature.code) return null;
                        
                        return (
                          <Grid item xs={6} md={4} key={feature.code || `feature-${featureIndex}`}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={selectedFeatureIds.includes(feature.code)}
                                  onChange={() => {
                                    const newSelected = selectedFeatureIds.includes(feature.code)
                                      ? selectedFeatureIds.filter(id => id !== feature.code)
                                      : [...selectedFeatureIds, feature.code];
                                    setSelectedFeatureIds(newSelected);
                                  }}
                                  size="small"
                                  sx={{
                                    color: 'rgba(29, 27, 32, 0.5)',
                                    '&.Mui-checked': { color: '#7100FF' }
                                  }}
                                />
                              }
                              label={
                                <Typography 
                                  variant="body2" 
                                  sx={{ 
                                    fontFamily: '"LTSaeada", "Roboto", sans-serif',
                                    color: selectedFeatureIds.includes(feature.code) ? '#7100FF' : '#1D1B20'
                                  }}
                                >
                                  {feature.name || 'Característica'}
                                </Typography>
                              }
                            />
                          </Grid>
                        );
                      })}
                    </Grid>
                    {index < additionalCharacters.length - 1 && <Divider sx={{ mt: 2 }} />}
                  </Box>
                );
              })}
            </>
          )}
          
          {/* Mostrar mensaje si no hay características disponibles después de cargar */}
          {Array.isArray(additionalCharacters) && additionalCharacters.length === 0 && (
            <Typography variant="body1" sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
              No hay características adicionales disponibles
            </Typography>
          )}
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 2, gap: 2, borderTop: '1px solid #eee' }}>
          <Button
            variant="outlined"
            onClick={onClose}
            sx={{
              borderRadius: '20px',
              px: 3,
              textTransform: 'none', // Evita mayúsculas
              borderColor: '#BDBDBD', // Gris claro
              color: '#3d3d3d',
              '&:hover': {
                borderColor: '#9E9E9E', // Gris un poco más oscuro al pasar el mouse
              },
              fontFamily: '"LTSaeada", "Roboto", sans-serif'
            }}
          >
            Cancelar
          </Button>

          <Button
            variant="contained"
            onClick={onSave}
            sx={{
              borderRadius: '20px',
              px: 3,
              backgroundColor: '#7100FF',
              textTransform: 'none', // Evita mayúsculas
              '&:hover': {
                backgroundColor: '#5c00d6',
              },
              fontFamily: '"LTSaeada", "Roboto", sans-serif'
            }}
          >
            Guardar
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default FeaturesModal;
